import classnames from 'classnames'
import { noop } from '../../utils/function'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { removeAdData, setAdData } from '../../store/ad/ad.actions'
import { selectIsCorrespondingPageAdConfigLoaded, selectPageIdForAdConfig } from '../../store/ad/ad.selectors'
import { withEmptyFallbackErrorBoundary } from '../error/EmptyFallbackErrorBoundary'
import { ONE_SECOND_MS } from '../utils/date'
import { useAction } from '../utils/react-redux'
import { getAdIdByAdName } from './AdConfigService'
import s from './AdLoader.less'
import { AdService } from './AdService'

export const AD_STATUS_NONE = 'AD_STATUS_NONE'
export const AD_STATUS_LOADING = 'AD_STATUS_LOADING'
export const AD_STATUS_LOADED = 'AD_STATUS_LOADED'

const useAdLoader = ({ adId, keywordsSelector = noop, askData = false }) => {
  const [adStatus, setAdStatus] = useState(AD_STATUS_NONE)
  const execSetAdData = useAction(setAdData)
  const execRemoveAdData = useAction(removeAdData)
  const keywords = useSelector(keywordsSelector)

  useEffect(() => {
    // keywords selectors must return <></> if there is no keywords available
    if (keywords === null || !adId) return

    switch (adStatus) {
      case AD_STATUS_NONE: {
        setAdStatus(AD_STATUS_LOADING)

        AdService.createAd(adId)
          .then(() => AdService.setAdKeywords(adId, keywords))
          .then(() => AdService.loadAd(adId))
          .then(() => askData && AdService.getAdData(adId).then(data => execSetAdData(adId, data)))
          .then(() => setAdStatus(AD_STATUS_LOADED))
          .catch(error => console.error('AdLoader loading error', error))
        break
      }

      case AD_STATUS_LOADED: {
        setAdStatus(AD_STATUS_LOADING)
        execRemoveAdData(adId)

        AdService.setAdKeywords(adId, keywords)
          .then(() => AdService.reloadAd(adId))
          .then(() => askData && AdService.getAdData(adId).then(data => execSetAdData(adId, data)))
          .then(() => setAdStatus(AD_STATUS_LOADED))
          .catch(error => console.error('AdLoader reload', error))

        break
      }
    }
  }, [JSON.stringify(keywords)])

  useEffect(() => {
    // sometimes, the ad api does resolve or reject the promise, so we do not keep loading status more than 5s, because it blocks refresh
    let timeoutId
    if (adStatus === AD_STATUS_LOADING) {
      timeoutId = setTimeout(() => setAdStatus(AD_STATUS_LOADED), 5 * ONE_SECOND_MS)
    }
    return () => clearTimeout(timeoutId)
  }, [adStatus])

  useEffect(
    () => () => {
      if (adId) {
        AdService.deleteAd(adId).catch(error => console.error('AdLoader remove error', error))
        execRemoveAdData(adId)
      }
    },
    []
  )

  return adStatus === AD_STATUS_LOADED
}

export const BaseAdLoader = ({ adName, className, keywordsSelector, children, askData }) => {
  const pageId = useSelector(selectPageIdForAdConfig)
  const adId = getAdIdByAdName(adName, pageId)

  useAdLoader({ adId, keywordsSelector, askData })

  return (
    <>
      {children}
      <div id={adId} className={classnames(className, askData && s.hidden)} />
    </>
  )
}

export default withEmptyFallbackErrorBoundary(function AdLoader({ adName, ...restProps }) {
  const isConfigLoaded = useSelector(state => selectIsCorrespondingPageAdConfigLoaded(state, adName))

  if (!isConfigLoaded) return <></>

  return <BaseAdLoader key={adName} adName={adName} {...restProps} />
})
